import { resolveFalImageInputUrl } from "../specsImageUpload";
import type { DetectedImportParts } from "./types";

function resizeForVision(dataUrl: string, maxSize = 512): Promise<string> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(maxSize / image.width, maxSize / image.height, 1);
      const width = Math.round(image.width * scale);
      const height = Math.round(image.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) {
        resolve(dataUrl);
        return;
      }
      context.drawImage(image, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    };
    image.onerror = () => resolve(dataUrl);
    image.src = dataUrl;
  });
}

export async function detectImportParts(
  imageUrl: string,
  falApiKey: string
): Promise<DetectedImportParts> {
  if (!falApiKey) {
    throw new Error("fal.ai API key is required");
  }

  const { getCached, setCached } = await import("../falCache");
  const cached = await getCached(imageUrl, "detectImportParts", "gpt-4.1");
  if (cached) {
    return JSON.parse(cached) as DetectedImportParts;
  }

  const thumbnail = imageUrl.startsWith("data:")
    ? await resizeForVision(imageUrl)
    : await resolveFalImageInputUrl(imageUrl);

  const response = await fetch(
    "https://fal.run/openrouter/router/openai/v1/chat/completions",
    {
      method: "POST",
      headers: {
        Authorization: `Key ${falApiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "openai/gpt-4.1",
        messages: [
          {
            role: "user",
            content: [
              {
                type: "text",
                text: `Analyze this flat garment image.

Return whether these product layers are visually present:
- laces: drawstrings, cords, laces
- zips: zippers, zip pulls, zip tape
- hand_pockets: kangaroo pockets, hand pockets, side pockets

The body layer is always present.

Respond with ONLY JSON in this exact shape:
{"body":true,"laces":true/false,"zips":true/false,"hand_pockets":true/false}`,
              },
              {
                type: "image_url",
                image_url: { url: thumbnail },
              },
            ],
          },
        ],
        max_tokens: 256,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Part detection failed ${response.status}: ${await response.text()}`
    );
  }

  const data = await response.json();
  const output: string = data.choices?.[0]?.message?.content ?? "";
  const jsonMatch = output.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error("Failed to parse part detection response");
  }

  const parsed = JSON.parse(jsonMatch[0]);
  const result: DetectedImportParts = {
    body: true,
    laces: parsed.laces === true,
    zips: parsed.zips === true,
    hand_pockets: parsed.hand_pockets === true,
  };

  void setCached(
    imageUrl,
    "detectImportParts",
    "gpt-4.1",
    JSON.stringify(result)
  );
  return result;
}
